import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { CinematicHero } from '@/components/home/CinematicHero';
import { ImmediateProductMoment } from '@/components/home/ImmediateProductMoment';
import { ProblemSection } from '@/components/home/ProblemSection';
import { FloatingHeartsScene } from '@/components/three/FloatingHeartsScene';
import peaceBg from '@/assets/peace-clarity-bg.png';
import { SolutionSection } from '@/components/home/SolutionSection';
import { PremiumTemplateShowcase } from '@/components/home/PremiumTemplateShowcase';
import { JacKeySection } from '@/components/home/JacKeySection';
import { LivingTreeShowcase } from '@/components/home/LivingTreeShowcase';
import { SocialProofMinimal } from '@/components/home/SocialProofMinimal';
import { SocialProofMetrics } from '@/components/home/SocialProofMetrics';
import { PricingSectionV2 } from '@/components/home/PricingSectionV2';
import { TrustSafetySection } from '@/components/home/TrustSafetySection';
import { FinalCTASection } from '@/components/home/FinalCTASection';
import { HowToUseGuide } from '@/components/home/HowToUseGuide';
import { HomeSEOSection } from '@/components/home/HomeSEOSection';
import { ShareTheWordsSection } from '@/components/home/ShareTheWordsSection';
import { StickyMobileActionBar } from '@/components/mobile/StickyMobileActionBar';
import { ScrollReveal } from '@/components/home/ScrollReveal';
import { SectionDivider } from '@/components/home/SectionDivider';
import { FloatingMessageFragments } from '@/components/home/FloatingMessageFragments';
import { SelfAffirmationSection } from '@/components/home/SelfAffirmationSection';
import { AffirmationBridgeCTA } from '@/components/home/AffirmationBridgeCTA';
import { ExperienceLivingAnnouncement } from '@/components/home/ExperienceLivingAnnouncement';
import { RecipientReactions } from '@/components/home/RecipientReactions';
import { InAppBrowserBanner } from '@/components/InAppBrowserBanner';
import { useReferralCapture } from '@/hooks/useReferralCapture';
import { useDocumentHead } from '@/hooks/useDocumentHead';


export default function Index() {
  useDocumentHead({ path: '/' });
  // Capture ?ref= param from referral links
  useReferralCapture();
  return (
    <div className="min-h-screen flex flex-col relative z-0">
      {/* Persistent peace & clarity background */}
      <div
        className="fixed inset-0 -z-10 pointer-events-none opacity-30 dark:opacity-25"
        style={{
          backgroundImage: `url(${peaceBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />

      {/* Lightweight CSS floating hearts */}
      <FloatingHeartsScene />

      {/* Full-page emotional fragment layer — spans entire landing page */}
      <FloatingMessageFragments />

      <InAppBrowserBanner />
      <Header />

      
      <main className="flex-1 pb-20 md:pb-0 relative z-10">
        {/* 1. Cinematic Hero — Video-first emotional hook */}
        <CinematicHero />

        <SectionDivider variant="fade" />

        {/* Self-Affirmation Section */}
        <ScrollReveal>
          <SelfAffirmationSection />
        </ScrollReveal>

        <SectionDivider variant="glow" />

        {/* 2. Immediate Product Moment */}
        <ScrollReveal>
          <ImmediateProductMoment />
        </ScrollReveal>

        <SectionDivider variant="glow" />

        {/* 2b. Experience a Living Announcement — auto-playing demo */}
        <ScrollReveal variant="fadeUp">
          <ExperienceLivingAnnouncement />
        </ScrollReveal>

        <SectionDivider variant="glow" />



        {/* 3. The Problem */}
        <ScrollReveal>
          <ProblemSection />
        </ScrollReveal>

        <SectionDivider variant="fade" />

        {/* 4. The Solution (How It Works) */}
        <ScrollReveal variant="fadeUp">
          <SolutionSection />
        </ScrollReveal>

        <SectionDivider variant="glow" />

        {/* 5. Premium Template Showcase */}
        <ScrollReveal variant="fadeIn">
          <PremiumTemplateShowcase />
        </ScrollReveal>

        <SectionDivider variant="fade" />

        {/* 6. JacKey AI Section */}
        <ScrollReveal>
          <JacKeySection />
        </ScrollReveal>

        <SectionDivider variant="glow" />

        {/* 7. Living Tree */}
        <ScrollReveal variant="scaleUp">
          <LivingTreeShowcase />
        </ScrollReveal>

        <SectionDivider variant="glow" />

        {/* 8. Social Proof Metrics */}
        <ScrollReveal variant="fadeUp">
          <SocialProofMetrics />
        </ScrollReveal>

        <SectionDivider variant="fade" />

        {/* 9. Social Proof Testimonials */}
        <ScrollReveal>
          <SocialProofMinimal />
        </ScrollReveal>

        <SectionDivider variant="fade" />

        {/* 9b. What Recipients Are Saying */}
        <ScrollReveal variant="fadeUp">
          <RecipientReactions />
        </ScrollReveal>

        <SectionDivider variant="glow" />



        {/* Affirmation → Message Bridge CTA */}
        <ScrollReveal variant="fadeUp">
          <AffirmationBridgeCTA />
        </ScrollReveal>

        <SectionDivider variant="fade" />

        {/* How to Use Guide */}
        <ScrollReveal>
          <HowToUseGuide />
        </ScrollReveal>

        <SectionDivider variant="glow" />

        {/* 10. Pricing */}
        <ScrollReveal variant="fadeUp">
          <PricingSectionV2 />
        </ScrollReveal>

        <SectionDivider variant="fade" />

        {/* Share the Words — Referral Tiers */}
        <ScrollReveal variant="fadeUp">
          <ShareTheWordsSection />
        </ScrollReveal>

        <SectionDivider variant="glow" />

        {/* 11. Trust & Safety */}
        <ScrollReveal>
          <TrustSafetySection />
        </ScrollReveal>

        {/* SEO Content Section */}
        <HomeSEOSection />

        {/* 12. Final CTA */}
        <ScrollReveal>
          <FinalCTASection />
        </ScrollReveal>
      </main>

      <Footer />
      <StickyMobileActionBar />
    </div>
  );
}
